import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "../utils/seo";
import { useJsonLd, organizationSchema } from "../utils/schema";
import "../home-editorial.css";

const SERVICES = [
  "Title & ownership research",
  "Right-of-way, leases & easements",
  "Permitting & crossing coordination",
  "Appraisal coordination & acquisition budgets",
  "GIS mapping & field-data integration",
  "Project management & survey access",
];

export default function OnwardLandSolutionsHome() {
  useSEO({
    title: "Onward Land Solutions | Land Decisions & Project Execution",
    description: "PathScore route evaluation, AreaScore site evaluation and experienced land professionals for energy and infrastructure. Talk to Onward about the land work.",
    canonicalPath: "/",
    ogImage: "https://www.onwardland.com/brand/onward-approved-social.png",
  });
  useJsonLd("ld-organization", organizationSchema);

  return (
    <div className="onward-home" data-testid="onward-home">
      <section className="home-hero" style={{ backgroundImage: "linear-gradient(90deg,rgba(9,10,12,.84),rgba(9,10,12,.70)),url('/brand/onward-hero-corridor.jpg')" }} data-testid="home-hero">
        <div className="home-inner">
          <h1 data-testid="hero-h1">Better land decisions.<br />Stronger project execution.</h1>
          <p data-testid="hero-lede">Evaluate the route. Understand the land. Move the project forward. Onward combines PathScore route evaluation, AreaScore site evaluation and experienced land professionals to support energy and infrastructure projects.</p>
          <div className="home-actions">
            <Link to="/score-a-corridor?source=home_first_look" className="home-button" data-testid="home-first-look-cta"><span>Get a Complimentary PathScore First Look</span><ArrowRight size={17} aria-hidden="true" /></Link>
            <Link to="/contact?source=home_hero_primary" className="home-text-link home-text-link--light" data-testid="hero-cta-primary">Talk to Onward</Link>
          </div>
          <p className="home-offer-note" data-testid="home-first-look-scope">Complimentary preliminary screening within PathScore. Full PathScore is separately scoped paid work.</p>
        </div>
      </section>

      <section className="home-products" data-testid="home-products">
        <div className="home-inner home-editorial-columns">
          <article data-testid="home-pathscore">
            <h2>PathScore</h2>
            <h3>Find the route worth pursuing.</h3>
            <p>The shortest route can create the hardest land problems. Compare practical alternatives, flag ownership and crossing issues, and understand the permitting and land-cost questions before committing further.</p>
            <p className="home-product-support" data-testid="home-pathscore-deliverables">Route comparison. Selected-route title. A plan for the land work.</p>
            <Link to="/path-score?source=home_product_card" className="home-text-link" data-testid="home-pathscore-product-link">Explore PathScore <ArrowRight size={16} aria-hidden="true" /></Link>
          </article>
          <article data-testid="home-areascore">
            <h2>AreaScore</h2>
            <h3>Find the site worth pursuing.</h3>
            <p>Screen a property or search area for ownership, access, nearby infrastructure and permitting considerations. Focus further due diligence on the sites that fit your project.</p>
            <Link to="/area-score?source=home_product_card" className="home-text-link" data-testid="home-areascore-product-link">Explore AreaScore <ArrowRight size={16} aria-hidden="true" /></Link>
          </article>
        </div>
      </section>

      <section className="home-services" data-testid="home-services">
        <div className="home-inner">
          <h2>The land team to carry it forward.</h2>
          <p className="home-section-intro">From the first review through acquisition and closeout, Onward coordinates the people, records and fieldwork.</p>
          <ul className="home-service-list" data-testid="home-services-list">{SERVICES.map((service, index) => <li key={service} data-testid={`home-service-${index + 1}`}>{service}</li>)}</ul>
          <p className="home-talent-line" data-testid="home-talent-copy">Need additional people? We also provide experienced contract land professionals.</p>
          <Link to="/talent-network?view=client&source=home_talent_primary#client-request" className="home-text-link" data-testid="talent-request-link">Explore the Talent Network <ArrowRight size={16} aria-hidden="true" /></Link>
          <p className="home-industry-line" data-testid="home-industry-line">Pipelines &amp; Midstream · Transmission · Renewables &amp; Storage · Private Power &amp; Data Infrastructure · Water &amp; Utilities</p>
        </div>
      </section>

      <section className="home-closing" data-testid="home-final-cta">
        <div className="home-inner">
          <h2>Tell us what you are building.</h2>
          <Link to="/contact?source=home_final_cta" className="home-button" data-testid="final-cta-primary">Talk to Onward <ArrowRight size={17} aria-hidden="true" /></Link>
        </div>
      </section>
    </div>
  );
}